import React from 'react';

import { InfoBannerArrow, InfoBannerIconWrapper, InfoBannerMessage, InfoBannerWrapper } from './info-banner-style';
import type { BannerArrowCorner } from './info-banner-style';

export interface InfoBannerProps {
  /** Texto del mensaje a mostrar */
  message: string;
  /** Icono personalizado. Si no se proporciona, se usa el icono de chat por defecto */
  icon?: React.ReactNode;
  /** Color de fondo del banner */
  backgroundColor?: string;
  /** Color del borde */
  borderColor?: string;
  /** Grosor del borde en px */
  borderWidth?: number;
  /** Radio del borde en px */
  borderRadius?: number;
  /** Color del texto */
  textColor?: string;
  /** Tamaño del texto en px */
  fontSize?: number;
  /** Anchura del banner en px */
  width?: number;
  /** Distancia desde la parte superior de la pantalla en px. Si se define, `bottom` se ignora */
  top?: number;
  /** Distancia desde la parte inferior de la pantalla en px. Se ignora si `top` está definido */
  bottom?: number;
  /** Distancia desde el borde derecho de la pantalla en px */
  right?: number;
  /** Duración de la animación de entrada en ms */
  entryDuration?: number;
  /** Tiempo que el banner permanece visible en ms */
  visibleDuration?: number;
  /** Duración de la animación de salida en ms */
  exitDuration?: number;
  /** Esquina donde se muestra la flecha indicadora. Si no se define, no se muestra */
  arrowCorner?: BannerArrowCorner;
  /** Color de la flecha. Por defecto usa `borderColor` */
  arrowColor?: string;
  /** Callback al hacer click sobre el banner */
  onClick?: () => void;
}

const InfoBanner: React.FC<InfoBannerProps> = ({
  message,
  icon,
  backgroundColor = '#e7efe7',
  borderColor = '#138a1f',
  borderWidth = 4,
  borderRadius = 18,
  textColor = '#137a1a',
  fontSize = 16,
  width = 320,
  top,
  bottom,
  right = 20,
  entryDuration = 2400,
  visibleDuration = 6000,
  exitDuration = 3600,
  arrowCorner,
  arrowColor,
  onClick,
}) => {
  const totalDuration = entryDuration + visibleDuration + exitDuration;
  const entryPct = Math.round((entryDuration / totalDuration) * 100);
  const visibleEndPct = Math.round(((entryDuration + visibleDuration) / totalDuration) * 100);

  return (
    <InfoBannerWrapper
      role="status"
      aria-live="polite"
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
      $backgroundColor={backgroundColor}
      $borderColor={borderColor}
      $borderWidth={borderWidth}
      $borderRadius={borderRadius}
      $width={width}
      $top={top ?? (bottom === undefined ? 40 : undefined)}
      $bottom={bottom}
      $right={right}
      $entryPct={entryPct}
      $visibleEndPct={visibleEndPct}
      $totalDuration={totalDuration}
      $fontSize={fontSize}
    >
      {arrowCorner && (
        <InfoBannerArrow $corner={arrowCorner} aria-hidden="true">
          <svg
            viewBox="0 0 16 16"
            width="18"
            height="18"
            fill="none"
            stroke={arrowColor ?? borderColor}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="13" x2="13" y2="3" />
            <polyline points="7,3 13,3 13,9" />
          </svg>
        </InfoBannerArrow>
      )}
      {icon !== null && <InfoBannerIconWrapper>{icon}</InfoBannerIconWrapper>}

      <InfoBannerMessage $textColor={textColor} $fontSize={fontSize}>
        {message}
      </InfoBannerMessage>
    </InfoBannerWrapper>
  );
};

export default InfoBanner;
